import PropTypes from 'prop-types';

import {
  Box, Flex, Heading, Text, useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';
import Link from '@salesforce/retail-react-app/app/components/link';

import {Component} from '@amblique/king-living/app/components/contentful/component';
import {Link as ContentfulLink} from '@amblique/king-living/app/contentful/models/link';
import {ShopTheLookEmbedProvider} from '@amblique/king-living/app/contentful/models/shop-the-look-embed-context';
import {formatBlogPublishedDate} from '@amblique/king-living/app/contentful/utils/format-blog-published-date';

/**
 * Preserve Chakra multi-part className while appending stable BEM hooks for QA / downstream CSS.
 * @param {Record<string, unknown> | undefined} part
 * @param {string} semanticClass
 */
function mergeBlogSummaryCx(part, semanticClass) {
  if (!part) return {className: semanticClass};
  const {className: chakraClass, ...rest} = part;

  return {...rest, className: [semanticClass, chakraClass].filter(Boolean).join(' ')};
}

/**
 * BlogSummary - Contentful component for displaying blog summary cards
 * Auto-fetches full Blog data if component is incomplete using CONTENTFUL_METADATA
 *
 * @param {Object} props - Component props
 * @param {Object} props.component - Contentful Blog data
 * @param {Object} props.component.blogTileImage - Blog tile image component
 * @param {string} props.component.blogCategory - Blog category text
 * @param {string} props.component.title - Blog title
 * @param {string} [props.component.description] - Shown below title only when gridSectionCta is inlined
 * @param {string[]} props.component.tags - Blog tags array
 * @param {string} props.component.url - URL to link to (applied to image)
 * @param {string} [props.component.datePublished] - Shown beside category only when gridSectionCta is inlined
 * @param {boolean} [props.isComplete] - Whether component has all fields
 * @param {boolean} [props.isFetching] - Whether additional data is being fetched
 * @param {string} [props.variant] - Style variant ('featured' for horizontal layout)
 * @param {Object} [props.gridSectionCta] - Blog Grid section CTA to render inline when URLs match this card
 * @returns {React.ReactElement}
 */

export const BlogSummary = ({
  component,
  slug,
  isComplete: isCompleteProp,
  isFetching: isFetchingProp = false,
  variant,
  className,
  gridSectionCta,
  allowCoverSwipe = false,
  ...props
}) => {
  const {blogTileImage, blogCategory, title, description, contentfulMetadata, url: componentUrl, datePublished} =
    component;
  const tags = contentfulMetadata?.tags || [];
  const styles = useMultiStyleConfig('contentful/blog-summary', {component: component, variant});

  const publishedDateLabel = formatBlogPublishedDate(datePublished);
  const showEyebrowPublishedDate = Boolean(gridSectionCta && publishedDateLabel);
  const descriptionText = typeof description === 'string' ? description.trim() : '';
  const showInlineDescription = Boolean(gridSectionCta && descriptionText);

  const titleTrailingMargin = showInlineDescription
    ? {base: 4, md: 6}
    : gridSectionCta
      ? variant === 'featured'
        ? 4
        : 2
      : undefined;

  const descriptionTrailingMargin = showInlineDescription ? {base: 6, md: 12} : undefined;

  const url = slug || componentUrl;

  const isFeaturedGridShopTheLook =
    variant === 'featured' && blogTileImage?.__typename === 'ShopTheLook';

  const featuredRootClassName = isFeaturedGridShopTheLook
    ? 'shop-the-look--featured-blog-grid-tile'
    : undefined;

  const rootSemantic = [
    'blog-summary',
    variant === 'featured' && 'blog-summary--featured',
    featuredRootClassName,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const contentClassName = [
    'blog-summary__content',
    isFeaturedGridShopTheLook && 'blog-summary__content--featured-shop-the-look',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <ShopTheLookEmbedProvider>
      <Box {...mergeBlogSummaryCx(styles.container, rootSemantic)} {...props}>
        {/* Blog Tile Image - wrapped with Link if url exists */}
        {blogTileImage && (
          <Box data-blog-summary-cover="" {...mergeBlogSummaryCx(styles.imageContainer, 'blog-summary__cover')}>
            {url ? (
              <Link
                className={allowCoverSwipe ? undefined : 'swiper-no-swiping'}
                to={url}
                aria-label={title}
                {...styles.imageLink}
              >
                <Component component={blogTileImage} {...styles.image} />
              </Link>
            ) : (
              <Component component={blogTileImage} {...styles.image} />
            )}
          </Box>
        )}

        {/* Blog Content */}
        <Box {...mergeBlogSummaryCx(styles.content, contentClassName)}>
          {(blogCategory || showEyebrowPublishedDate) && (
            showEyebrowPublishedDate ? (
              <Flex {...mergeBlogSummaryCx(styles.metaRow, 'blog-summary__meta-row')}>
                {blogCategory ? (
                  <Text {...mergeBlogSummaryCx(styles.category, 'blog-summary__category')}>{blogCategory}</Text>
                ) : null}
                <Text {...mergeBlogSummaryCx(styles.publishedDate, 'blog-summary__published-date')}>
                  {publishedDateLabel}
                </Text>
              </Flex>
            ) : (
              <Text {...mergeBlogSummaryCx(styles.category, 'blog-summary__category')} mb={2}>
                {blogCategory}
              </Text>
            )
          )}
          {title && (
            url ? (
              <Link
                className={allowCoverSwipe ? undefined : 'swiper-no-swiping'}
                to={url}
                {...mergeBlogSummaryCx(styles.titleLink, 'blog-summary__title-link')}
                aria-label={title}
                mb={titleTrailingMargin}
              >
                <Heading as="h3" {...mergeBlogSummaryCx(styles.title, 'blog-summary__title')} mb={0}>
                  {title}
                </Heading>
              </Link>
            ) : (
              <Heading as="h3" {...mergeBlogSummaryCx(styles.title, 'blog-summary__title')} mb={titleTrailingMargin}>
                {title}
              </Heading>
            )
          )}
          {showInlineDescription ? (
            <Text
              {...mergeBlogSummaryCx(styles.description, 'blog-summary__description')}
              mb={descriptionTrailingMargin}
            >
              {descriptionText}
            </Text>
          ) : null}
          {gridSectionCta ? (
            <Box {...mergeBlogSummaryCx(styles.ctaSlot, 'blog-summary__grid-section-cta')}>
              <ContentfulLink component={gridSectionCta} isComplete={isCompleteProp} isFetching={isFetchingProp} />
            </Box>
          ) : null}
        </Box>

        {/* Tags from Contentful metadata */}
        {tags && tags.length > 0 && (
          <Flex {...mergeBlogSummaryCx(styles.tagsContainer, 'blog-summary__tags')}>
            {tags.map((tag) => (
              <Box key={tag.id} {...mergeBlogSummaryCx(styles.tag, 'blog-summary__tag')}>
                <Text {...mergeBlogSummaryCx(styles.tagText, 'blog-summary__tag-text')}>{tag.name}</Text>
              </Box>
            ))}
          </Flex>
        )}
      </Box>
    </ShopTheLookEmbedProvider>
  );
};

BlogSummary.displayName = 'BlogSummary';
BlogSummary.propTypes = {
  component: PropTypes.shape({
    __typename: PropTypes.string,
    sys: PropTypes.shape({
      id: PropTypes.string,
    }),
    blogTileImage: PropTypes.object,
    blogCategory: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    contentfulMetadata: PropTypes.shape({
      tags: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
      })),
    }),
    url: PropTypes.string,
    datePublished: PropTypes.string,
  }).isRequired,
  slug: PropTypes.string,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
  variant: PropTypes.string,
  className: PropTypes.string,
  gridSectionCta: PropTypes.object,
  /** CF-050 carousel — allow touch swipe on cover/title; tap still navigates when movement is small. */
  allowCoverSwipe: PropTypes.bool,
};
